import { Helmet } from 'react-helmet-async';
import { useLang } from '../i18n/LanguageContext';

const SITE_ORIGIN = 'https://edelnad.com';

// English JSON-LD @graph — mirrors the original index.html structured data.
// Injected via Helmet on English home pages so each language owns exactly one
// @graph (no duplicate on /de, which gets DE_JSON_LD instead).
const EN_JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ProfessionalService',
      '@id': 'https://edelnad.com/#business',
      name: 'EDELNAD Productions',
      url: 'https://edelnad.com/',
      description:
        'Video production company for conferences, medical congresses, summits and brand events — from one-camera shoots for small events to full production crews. Filming, on-site editing and on-location delivery, based in Berlin and working across Europe.',
      image: 'https://edelnad.com/images/services/conference-coverage.png',
      address: { '@type': 'PostalAddress', addressLocality: 'Berlin', addressCountry: 'DE' },
      areaServed: [
        'Germany', 'Austria', 'Denmark', 'Finland', 'Sweden', 'Netherlands',
        'United Kingdom', 'France', 'Portugal', 'Spain', 'Italy', 'Greece',
        'Cyprus', 'Turkey', 'Romania', 'Poland', 'Lithuania', 'Israel',
      ],
      founder: { '@id': 'https://edelnad.com/#amit' },
      knowsAbout: [
        'conference video production',
        'medical congress filming',
        'small event video production',
        'one-camera video shoots',
        'event highlight videos',
        'booth videos',
        'fireside chat production',
        'podcast production on location',
        'on-location video delivery',
        'on-site video editing',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Video production services',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Conference & Congress Coverage',
              description:
                'Full-service video production for multi-day conferences, medical congresses and industry summits — keynotes, panels, interviews and atmosphere. Includes fireside chats and podcast recordings on location, ready for distribution.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Booth Highlight Videos',
              description: 'High-end highlight films for exhibition booths and brand events.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'On-location Delivery',
              description:
                'On-location editing with 24-hour turnaround — finished clips delivered while the event is still happening.',
            },
          },
        ],
      },
    },
    {
      '@type': 'Person',
      '@id': 'https://edelnad.com/#amit',
      name: 'Amit Edelman',
      jobTitle: 'Director, Cinematographer & Editor',
      worksFor: { '@id': 'https://edelnad.com/#business' },
      homeLocation: { '@type': 'Place', name: 'Berlin, Germany' },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://edelnad.com/#website',
      url: 'https://edelnad.com/',
      name: 'EDELNAD Productions',
      publisher: { '@id': 'https://edelnad.com/#business' },
    },
    {
      '@type': 'FAQPage',
      '@id': 'https://edelnad.com/#faq',
      mainEntity: [
        { '@type': 'Question', name: 'Where are you based, and do you travel?', acceptedAnswer: { '@type': 'Answer', text: "We're based in Berlin and travel constantly — we've filmed events in 18 countries across Europe and beyond. If your event has a location, we'll be there." } },
        { '@type': 'Question', name: 'How fast can you deliver?', acceptedAnswer: { '@type': 'Answer', text: 'Standard delivery is within days of the event. If you need faster, we offer on-location editing with 24-hour turnaround — finished clips delivered while the event is still running.' } },
        { '@type': 'Question', name: 'What types of events do you cover?', acceptedAnswer: { '@type': 'Answer', text: 'Multi-day conferences, medical congresses, industry summits, exhibitions and brand events — anywhere from a single stage to a whole venue.' } },
        { '@type': 'Question', name: 'What kinds of videos do you make?', acceptedAnswer: { '@type': 'Answer', text: 'Event highlight films, daily recap clips, keynote and session coverage, speaker interviews, fireside chats and podcast recordings, booth videos and social-media cuts — plus brand films built from event footage.' } },
        { '@type': 'Question', name: 'Do you only cover large events?', acceptedAnswer: { '@type': 'Answer', text: 'Not at all — we scale the crew to the event. Small events and brand shoots often need just one cinematographer who shoots and edits; that\'s how we filmed BYRD, RavenDB and Waves Audio. Same standard, fewer people.' } },
        { '@type': 'Question', name: 'Do you bring your own crew and gear?', acceptedAnswer: { '@type': 'Answer', text: "Yes — we arrive fully equipped: camera, sound, lighting and an edit setup. Crew size depends on the brief; most projects need one or two people, and when wider coverage is needed we bring in trusted colleagues we've worked with for years." } },
        { '@type': 'Question', name: 'Do you also do photography?', acceptedAnswer: { '@type': 'Answer', text: "Photography isn't our specialty — we'd rather tell you that than deliver something average. But after 20+ years in this business we know the best event photographers around, and we're happy to connect you with the right person for your event." } },
        { '@type': 'Question', name: 'How early should we book?', acceptedAnswer: { '@type': 'Answer', text: "Ideally 4–6 weeks before your event so we can plan coverage properly. That said, we've jumped in days before an event — ask, and we'll tell you honestly if we can make it work." } },
      ],
    },
  ],
};

// German JSON-LD @graph mirroring the English one.
// Injected only on /de pages so the English index.html JSON-LD isn't duplicated.
const DE_JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ProfessionalService',
      '@id': 'https://edelnad.com/#business',
      name: 'EDELNAD Productions',
      url: 'https://edelnad.com/de',
      description:
        'Videoproduktion für Konferenzen, medizinische Kongresse, Summits und Marken-Events — von Einzelkamera-Drehs für kleine Events bis zu ganzen Produktionsteams. Dreh, Vor-Ort-Schnitt und Vor-Ort-Zustellung, ansässig in Berlin, tätig in ganz Europa.',
      image: 'https://edelnad.com/images/services/conference-coverage.png',
      address: { '@type': 'PostalAddress', addressLocality: 'Berlin', addressCountry: 'DE' },
      areaServed: [
        'Deutschland', 'Österreich', 'Dänemark', 'Finnland', 'Schweden',
        'Niederlande', 'Großbritannien', 'Frankreich', 'Portugal', 'Spanien',
        'Italien', 'Griechenland', 'Zypern', 'Türkei', 'Rumänien', 'Polen',
        'Litauen', 'Israel',
      ],
      founder: { '@id': 'https://edelnad.com/#amit' },
      knowsAbout: [
        'Kongress-Videoproduktion',
        'medizinische Kongressfilmung',
        'Videoproduktion für kleine Events',
        'Einzelkamera-Drehs',
        'Event-Highlight-Videos',
        'Stand-Videos',
        'Fireside-Chat-Produktion',
        'Podcast-Produktion vor Ort',
        'Vor-Ort-Videowiedergabe',
        'Vor-Ort-Videoschnitt',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Videoproduktionsleistungen',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Konferenz- & Kongress-Berichterstattung',
              description:
                'Full-Service-Videoproduktion für mehrtägige Konferenzen, medizinische Kongresse und Branchen-Summits — Keynotes, Panels, Interviews und Atmosphäre. Inklusive Fireside Chats und Podcast-Aufnahmen vor Ort, verteilerfertig.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Stand-Highlight-Videos',
              description: 'Hochwertige Highlight-Filme für Messestände und Marken-Events.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Vor-Ort-Zustellung',
              description:
                'Vor-Ort-Schnitt mit 24-Stunden-Zusage — fertige Clips, geliefert während das Event noch läuft.',
            },
          },
        ],
      },
    },
    {
      '@type': 'Person',
      '@id': 'https://edelnad.com/#amit',
      name: 'Amit Edelman',
      jobTitle: 'Regisseur, Kameramann & Editor',
      worksFor: { '@id': 'https://edelnad.com/#business' },
      homeLocation: { '@type': 'Place', name: 'Berlin, Deutschland' },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://edelnad.com/#website',
      url: 'https://edelnad.com/de',
      name: 'EDELNAD Productions',
      publisher: { '@id': 'https://edelnad.com/#business' },
    },
    {
      '@type': 'FAQPage',
      '@id': 'https://edelnad.com/#faq',
      inLanguage: 'de',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Wo sitzen Sie und reisen Sie auch an?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Wir sitzen in Berlin und sind ständig unterwegs. Wir haben Events in 18 Ländern in ganz Europa und darüber hinaus gefilmt. Wenn Ihr Event einen Ort hat, sind wir da.',
          },
        },
        {
          '@type': 'Question',
          name: 'Wie schnell können Sie liefern?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Standard-Lieferung erfolgt innerhalb weniger Tage nach dem Event. Wenn es schneller sein muss, bieten wir Vor-Ort-Schnitt mit 24-Stunden-Zusage: fertige Clips, geliefert während das Event noch läuft.',
          },
        },
        {
          '@type': 'Question',
          name: 'Welche Arten von Events begleiten Sie?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Mehrtägige Konferenzen, medizinische Kongresse, Branchen-Summits, Messen und Marken-Events — von einer einzelnen Bühne bis zu einer ganzen Location.',
          },
        },
        {
          '@type': 'Question',
          name: 'Welche Arten von Videos produzieren Sie?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Event-Highlight-Filme, tägliche Recap-Clips, Keynote- und Session-Coverage, Speaker-Interviews, Fireside Chats und Podcast-Aufnahmen, Stand-Videos und Social-Media-Schnitte — sowie Markenfilme aus Event-Footage.',
          },
        },
        {
          '@type': 'Question',
          name: 'Begleiten Sie nur große Events?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Überhaupt nicht. Wir skalieren das Team nach dem Event. Kleine Events und Marken-Drehs brauchen oft nur eine:n Kameramann:in, die/der filmt und schneidet; so haben wir RavenDB und Waves Audio gefilmt. Gleicher Standard, weniger Personen.',
          },
        },
        {
          '@type': 'Question',
          name: 'Bringen Sie eigenes Team und eigenes Equipment mit?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Ja, wir kommen vollständig ausgestattet: Kamera, Ton, Licht und ein Schnitt-Setup. Die Teamgröße hängt vom Briefing ab; die meisten Projekte brauchen ein oder zwei Personen, und wenn breitere Coverage nötig ist, holen wir vertraute Kolleg:innen dazu, mit denen wir schon jahrelang arbeiten.',
          },
        },
        {
          '@type': 'Question',
          name: 'Machen Sie auch Fotografie?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Fotografie ist nicht unsere Spezialität. Wir sagen Ihnen das lieber, als etwas Durchschnittliches zu liefern. Aber nach über 20 Jahren in der Branche kennen wir die besten Event-Fotograf:innen und verbinden Sie gerne mit der richtigen Person für Ihr Event.',
          },
        },
        {
          '@type': 'Question',
          name: 'Wie früh sollten wir buchen?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Idealerweise 4–6 Wochen vor Ihrem Event, damit wir die Coverage richtig planen können. Trotzdem sind wir schon Tage vor einem Event eingesprungen. Fragen Sie — und wir sagen Ihnen ehrlich, ob wir es schaffen.',
          },
        },
      ],
    },
  ],
};

// Path without the language prefix ('/' for both '/' and '/de').
function stripLang(pathname) {
  if (pathname === '/de') return '/';
  if (pathname.startsWith('/de/')) return pathname.slice(3);
  return pathname;
}

// hreflang + canonical link tags for the current page. `langPath` is the
// language-agnostic path (e.g. '/', '/work/foo', '/impressum').
export function hreflangTags(langPath) {
  const en = `${SITE_ORIGIN}${langPath === '/' ? '/' : langPath}`;
  const de = `${SITE_ORIGIN}/de${langPath === '/' ? '' : langPath}`;
  return (
    <>
      <link rel="canonical" href={langPath.startsWith('/de') ? de : en} />
      <link rel="alternate" hrefLang="en" href={en} />
      <link rel="alternate" hrefLang="de" href={de} />
      <link rel="alternate" hrefLang="x-default" href={en} />
    </>
  );
}

// Shared Home <Helmet> — title/description/OG all language-aware.
export function HomeSeo() {
  const { lang } = useLang();
  const langPath = lang === 'de' ? '/de' : '/';

  const enTitle = 'EDELNAD Productions | Event Video Production — Berlin';
  const enDesc =
    'Video production for conferences, congresses and brand events — from one-camera shoots to full crews. On-site editing and on-location delivery, based in Berlin.';
  const deTitle = 'EDELNAD Productions | Event-Videoproduktion — Berlin';
  const deDesc =
    'Videoproduktion für Konferenzen, Kongresse und Marken-Events — von Einzelkamera-Drehs bis zu ganzen Teams. Vor-Ort-Schnitt und Vor-Ort-Zustellung, ansässig in Berlin.';

  const title = lang === 'de' ? deTitle : enTitle;
  const desc = lang === 'de' ? deDesc : enDesc;
  const canonical = `${SITE_ORIGIN}${lang === 'de' ? '/de' : '/'}`;
  const ogImage = `${SITE_ORIGIN}/images/services/conference-coverage.png`;
  const jsonLd = lang === 'de' ? DE_JSON_LD : EN_JSON_LD;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={`${SITE_ORIGIN}/`} />
      <link rel="alternate" hrefLang="de" href={`${SITE_ORIGIN}/de`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_ORIGIN}/`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="EDELNAD Productions" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={lang === 'de' ? 'de_DE' : 'en_US'} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
    </Helmet>
  );
}

// Used by NotFound and legal pages to set <html lang> + hreflang.
export function HtmlLangTag() {
  const { lang } = useLang();
  return (
    <Helmet>
      <html lang={lang} />
    </Helmet>
  );
}

export { SITE_ORIGIN, DE_JSON_LD, EN_JSON_LD };
